/**
 * Game WebSocket Client — singleton connection to game-ws-relay-service
 *
 * Manages a single WebSocket connection with:
 * - Auto-reconnect with exponential backoff
 * - Subscription-based message routing (lobby + room)
 * - Connection lifecycle events
 *
 * The WS relay allows ONE lobby subscription + ONE room subscription
 * per connection. Switching subscriptions auto-unsubscribes the previous.
 */

import {
  isWSServerMessage,
  type WSClientMessage,
  type WSServerMessage,
} from "./types";

const INITIAL_RECONNECT_DELAY_MS = 1_000;
const MAX_RECONNECT_DELAY_MS = 30_000;
const JITTER_FACTOR = 0.3;
const GAME_PLATFORM_WS_PATH = "/api/v2/game-platform/ws";
export type WSRouteMode = "legacy" | "v2";

export type WSMessageHandler = (message: WSServerMessage) => void;
export type WSConnectionHandler = () => void;

export interface WSClientOptions {
  readonly onMessage: WSMessageHandler;
  readonly onConnect?: WSConnectionHandler;
  readonly onDisconnect?: WSConnectionHandler;
}

export interface WSClient {
  readonly send: (message: WSClientMessage) => void;
  readonly close: () => void;
  readonly isConnected: () => boolean;
}

/**
 * Derive a WebSocket URL from the multiplayer service base URL.
 *
 * Converts `https://` → `wss://` and `http://` → `ws://`,
 * then appends the WS relay path.
 */
export function deriveWsUrl(
  baseUrl: string,
  _routeMode: WSRouteMode = "v2",
): string {
  const wsBase = baseUrl
    .replace(/^https:\/\//, "wss://")
    .replace(/^http:\/\//, "ws://");
  return `${wsBase}${GAME_PLATFORM_WS_PATH}`;
}

/**
 * Create a WebSocket client that connects to the game-ws-relay-service.
 *
 * The connection is established immediately and auto-reconnects on failure.
 * Authentication is handled via httpOnly cookies (same-origin).
 */
export function createWSClient(
  wsUrl: string,
  options: WSClientOptions,
): WSClient {
  let ws: WebSocket | null = null;
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  let reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
  let closed = false;

  function connect(): void {
    if (closed) return;

    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      reconnectDelay = INITIAL_RECONNECT_DELAY_MS;
      options.onConnect?.();
    };

    ws.onmessage = (event: MessageEvent<string>) => {
      try {
        const raw: unknown = JSON.parse(event.data);
        if (!isWSServerMessage(raw)) return;
        options.onMessage(raw);
      } catch {
        // Ignore malformed messages
      }
    };

    ws.onclose = () => {
      options.onDisconnect?.();
      scheduleReconnect();
    };

    ws.onerror = () => {
      // onclose will fire after onerror, triggering reconnect
    };
  }

  function scheduleReconnect(): void {
    if (closed || reconnectTimeout !== null) return;

    // Add ±30% jitter to prevent thundering herd on server restart
    const jitter = reconnectDelay * JITTER_FACTOR * (Math.random() * 2 - 1);
    const delay = Math.max(0, reconnectDelay + jitter);

    reconnectTimeout = setTimeout(() => {
      reconnectTimeout = null;
      connect();
    }, delay);

    reconnectDelay = Math.min(reconnectDelay * 2, MAX_RECONNECT_DELAY_MS);
  }

  function send(message: WSClientMessage): void {
    if (ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }

  function close(): void {
    closed = true;
    if (reconnectTimeout !== null) {
      clearTimeout(reconnectTimeout);
      reconnectTimeout = null;
    }
    if (ws !== null) {
      ws.onclose = null; // Prevent reconnect on intentional close
      ws.close();
      ws = null;
    }
  }

  function isConnected(): boolean {
    return ws?.readyState === WebSocket.OPEN;
  }

  connect();

  return { send, close, isConnected };
}
